import { motion } from "framer-motion";
import { Award, ShieldCheck, Brain } from "lucide-react";

const GROUPS = [
  {
    label: "Security & Cloud",
    icon: ShieldCheck,
    accent: "blue",
    certs: [
      { code: "CISSP", body: "(ISC)²" },
      { code: "CCSP", body: "(ISC)²" },
      { code: "CEH", body: "EC-Council" },
      { code: "Security+", body: "CompTIA" },
      { code: "CCSK", body: "Cloud Security Alliance" },
      { code: "AZ-500", body: "Microsoft Azure Security" },
      { code: "SC-100", body: "Microsoft Cybersecurity Architect" },
      { code: "SC-200", body: "Microsoft SecOps Analyst" },
    ],
  },
  {
    label: "AI Security",
    icon: Brain,
    accent: "cyan",
    certs: [
      { code: "SecAI+", body: "CompTIA" },
      { code: "AAISM", body: "ISACA · Advanced in AI Security Mgmt" },
      { code: "CAISR", body: "Certified AI Security & Risk" },
      { code: "AIGP", body: "IAPP · AI Governance" },
      { code: "GenAI Defense", body: "Workshop · ATT&CK-aligned" },
    ],
  },
];

const accentMap = {
  blue: {
    dot: "#60A5FA",
    border: "rgba(96,165,250,0.30)",
    soft: "rgba(96,165,250,0.08)",
    glow: "rgba(37,99,235,0.18)",
  },
  cyan: {
    dot: "#22D3EE",
    border: "rgba(34,211,238,0.30)",
    soft: "rgba(34,211,238,0.08)",
    glow: "rgba(6,182,212,0.18)",
  },
};

export default function Certifications() {
  return (
    <section
      id="certifications"
      data-testid="certifications-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Backdrop */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(700px 380px at 90% 0%, rgba(37,99,235,0.08), transparent 60%), radial-gradient(700px 380px at 5% 100%, rgba(6,182,212,0.06), transparent 65%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-blue-500" />
          <span className="label-eyebrow">03 — Certifications</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-14 md:mb-16 items-end">
          <h2 className="md:col-span-8 font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] tracking-tighter leading-[1.04] text-white">
            Vetted credentials. <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg,#67E8F9 0%,#60A5FA 50%,#A5B4FC 100%)",
              }}
            >
              Operational fluency.
            </span>
          </h2>
          <p className="md:col-span-4 text-slate-400 leading-relaxed text-base md:text-lg">
            Held, taught and operationalised — the credentials your auditors
            screen for, grouped by the practice they belong to.
          </p>
        </div>

        {/* Groups */}
        <div className="space-y-12 md:space-y-14">
          {GROUPS.map((g, gi) => {
            const a = accentMap[g.accent];
            const Icon = g.icon;
            return (
              <div key={g.label} data-testid={`cert-group-${gi + 1}`}>
                {/* Group header */}
                <div
                  className="flex flex-wrap items-center justify-between gap-3 pb-5 mb-6 border-b"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-9 w-9 rounded-lg grid place-items-center"
                      style={{
                        background: a.soft,
                        border: `1px solid ${a.border}`,
                        color: a.dot,
                      }}
                    >
                      <Icon size={16} />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-white text-lg md:text-xl tracking-tight">
                        {g.label}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
                        Group {String(gi + 1).padStart(2, "0")} ·{" "}
                        {String(g.certs.length).padStart(2, "0")} credentials
                      </div>
                    </div>
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5"
                    style={{ color: a.dot }}
                  >
                    <span className="h-1 w-1 rounded-full" style={{ background: a.dot }} />
                    held &amp; current
                  </span>
                </div>

                {/* Badges */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 md:gap-4">
                  {g.certs.map((c, i) => (
                    <motion.div
                      key={c.code}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.4, delay: i * 0.04 }}
                      data-testid={`cert-${c.code.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group relative rounded-xl p-4 transition-all duration-300 hover:-translate-y-0.5 flex flex-col"
                      style={{
                        background:
                          "linear-gradient(180deg,rgba(10,12,19,0.85) 0%,rgba(7,10,18,0.95) 100%)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset",
                      }}
                    >
                      <div
                        aria-hidden
                        className="absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                        style={{
                          background: `radial-gradient(220px 140px at 80% 0%, ${a.glow}, transparent 70%)`,
                        }}
                      />
                      <div className="relative flex items-start justify-between mb-3">
                        <Award size={14} style={{ color: a.dot }} />
                        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-600">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="relative font-display font-bold text-white tracking-tight text-lg md:text-xl leading-none">
                        {c.code}
                      </div>
                      <div className="relative mt-2 font-mono text-[9px] uppercase tracking-[0.20em] text-slate-500 leading-snug">
                        {c.body}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
